/**
 * PortalGruppenList — the list of a customer's Portal-Berechtigungsgruppen with the
 * add / edit / rename / delete affordances.
 *
 *  - The 4 templates + custom groups are editable, renamable, deletable.
 *  - The "Nicht zugeordnet" system group renders READ-ONLY (non-deletable, auto-computed
 *    complement + all portal users).
 *  - Editing goes through the backend's GRANULAR calls: the editor's desired end-state is
 *    diffed against the group's current state, then category/member add/removes are issued
 *    and the returned { mode, groups } snapshot is adopted.
 *  - Additive-union visibility is explained inline.
 */

import { useMemo, useState } from 'react';
import { Lock, Pencil, Plus, Trash2, Users } from 'lucide-react';
import { Badge } from '../../../components/ui/Badge';
import { Dialog, Button } from '../../administration/components';
import { PortalGruppeEditor, type EditorDraft } from './PortalGruppeEditor';
import { memberRefOf } from '../api';
import { useDeletePortalGruppe, useSavePortalGruppe } from '../hooks';
import {
  IGB2B_FAMILIEN,
  type Igb2bKategorieDto,
  type PortalGroupDto,
  type PortalMemberOption,
} from '../types';

interface Props {
  subjectType: string;
  subjectId: string;
  gruppen: PortalGroupDto[];
  memberPool: PortalMemberOption[];
  kategorien: Igb2bKategorieDto[];
  canManage: boolean;
  onFeedback?: (msg: string) => void;
}

export function PortalGruppenList({
  subjectType,
  subjectId,
  gruppen,
  memberPool,
  kategorien,
  canManage,
  onFeedback,
}: Props) {
  const [editing, setEditing] = useState<PortalGroupDto | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PortalGroupDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useSavePortalGruppe(subjectType, subjectId);
  const del = useDeletePortalGruppe(subjectType, subjectId);

  const familieForCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const k of kategorien) m.set(k.branchencode, k.familie);
    return m;
  }, [kategorien]);

  const coveredFamilies = (codes: string[]): string[] => {
    const keys = new Set<string>();
    for (const c of codes) {
      const fam = familieForCode.get(c);
      if (fam) keys.add(fam);
    }
    return IGB2B_FAMILIEN.filter((f) => keys.has(f.key)).map((f) => f.label);
  };

  const saving = save.isPending;

  const handleSave = (draft: EditorDraft) => {
    setError(null);
    if (creating) {
      save.mutate(
        { groupId: null, draft },
        {
          onSuccess: () => {
            setCreating(false);
            onFeedback?.('Gruppe erstellt.');
          },
          onError: (e: Error) => setError(e.message),
        },
      );
    } else if (editing) {
      save.mutate(
        {
          groupId: editing.id,
          draft,
          current: {
            name: editing.name,
            branchencodes: editing.branchencodes,
            memberRefs: editing.members.map((m) => memberRefOf(m.grantId)),
          },
        },
        {
          onSuccess: () => {
            setEditing(null);
            onFeedback?.('Gruppe gespeichert.');
          },
          onError: (e: Error) => setError(e.message),
        },
      );
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-foreground">Berechtigungsgruppen</div>
          <p className="text-xs text-muted-foreground">
            Ein Portal-Nutzer sieht eine Police, wenn ihre Kategorie in mindestens einer seiner
            Gruppen liegt (additive Vereinigung).
          </p>
        </div>
        {canManage && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setError(null);
              setCreating(true);
            }}
          >
            <Plus className="size-4" /> Neue Gruppe
          </Button>
        )}
      </div>

      <ul className="flex flex-col gap-2" aria-label="Berechtigungsgruppen-Liste">
        {gruppen.map((g) => {
          const families = coveredFamilies(g.branchencodes);
          return (
            <li
              key={g.id}
              className="flex flex-col gap-2 rounded-xl border border-border bg-card p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">{g.name}</span>
                    {g.istSystem && (
                      <Badge variant="neutral">
                        <Lock className="size-3" /> System
                      </Badge>
                    )}
                    {g.istTemplate && <Badge variant="info">Vorlage</Badge>}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    <span className="tabular-nums">{g.branchencodes.length}</span> Kategorien
                    {families.length > 0 && <> · {families.join(', ')}</>}
                    {g.istSystem && g.branchencodes.length === 0 && (
                      <> · nur nicht zugeordnete Policen</>
                    )}
                  </div>
                </div>
                {!g.istSystem && canManage && (
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setError(null);
                        setEditing(g);
                      }}
                      aria-label={`Gruppe ${g.name} bearbeiten`}
                    >
                      <Pencil className="size-4" /> Bearbeiten
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-status-danger hover:bg-status-danger-bg"
                      onClick={() => setDeleteTarget(g)}
                      aria-label={`Gruppe ${g.name} löschen`}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <Users className="size-3.5" />
                {g.istSystem ? (
                  <span>Alle Portal-Nutzer (automatisch)</span>
                ) : g.members.length === 0 ? (
                  <span>Keine Mitglieder zugewiesen</span>
                ) : (
                  g.members.map((m) => (
                    <span
                      key={memberRefOf(m.grantId)}
                      className="inline-flex items-center rounded-md border border-border bg-muted px-1.5 py-0.5"
                    >
                      {m.label ?? 'Hauptkontakt'}
                    </span>
                  ))
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {(creating || editing) && (
        <PortalGruppeEditor
          gruppe={editing}
          kategorien={kategorien}
          memberPool={memberPool}
          saving={saving}
          error={error}
          onSave={handleSave}
          onClose={() => {
            setCreating(false);
            setEditing(null);
            setError(null);
          }}
        />
      )}

      <Dialog
        open={deleteTarget != null}
        onClose={() => {
          if (!del.isPending) setDeleteTarget(null);
        }}
        title="Gruppe löschen"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={del.isPending}>
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              disabled={del.isPending}
              onClick={() => {
                if (!deleteTarget) return;
                del.mutate(deleteTarget.id, {
                  onSuccess: () => {
                    setDeleteTarget(null);
                    onFeedback?.('Gruppe gelöscht.');
                  },
                  onError: (e: Error) => setError(e.message),
                });
              }}
            >
              {del.isPending ? 'Wird gelöscht …' : 'Löschen'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          Gruppe „{deleteTarget?.name}" wirklich löschen? Ihre Kategorien fallen dann in die
          Gruppe „Nicht zugeordnet" zurück.
        </p>
      </Dialog>
    </div>
  );
}
